import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/browser";
import { useAlert } from "react-alert";

import _ from "lodash";

import { makeStyles } from "@material-ui/styles";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Grid,
  Typography,
} from "@material-ui/core";

import { alpha } from "@material-ui/core/styles/colorManipulator";

import useInterval from "react-use/lib/useInterval";

import Editor from "react-simple-code-editor";

import { highlight, languages } from "prismjs";

import AddIcon from "@material-ui/icons/AddOutlined";

import clsx from "clsx";

import { getSyncStatusString, MemoTextField } from "../../../EventsPage.jsx";

import { SnapshotList } from "./SnapshotList";
import FilterList from "../../../FilterList.jsx";

import useFeatureFlags from "../../../hooks/useFeatureFlag";
import useCurrentEnvironment from "../../../hooks/use-environment";
import { useAllSaasDestinations } from "../../../hooks/useAllSaasDestinations";
import {
  destinationKeyToSlugMap,
  warehouseDestinations,
} from "../../../destinations/destinations";
import { useDestinationMetadataManager } from "../../../hooks/useDestinationMetadataManager";
import { EventType, libraryForEventType } from "../../../eventTypes";
import { getFeatureFlag, isNetworkFilter, makeRequest } from "../../../util";
import {
  EventDefinitionFilter,
  EventSnapshot,
  LoadedDestinationInfo,
  LoadedEventDefinition,
  SyncStatus,
} from "../../../types/global";

import AutocompletePropField from "../../../AutocompletePropField";
import { ChangeNetworkEventModal } from "./modals/ChangeNetworkEventModal";
import { RefreshIcon } from "../../../components/icons/themes/windows10";
import EventEditorHeaderAction from "./EventEditorHeaderAction";

// This is there is only a single instance of this object. This allows us to use
// memoization for the MemoTextField when passing in the shrink argument.
export const shrink = { shrink: true };
export const native = { native: true };

export const useEventEditorStyles = makeStyles((theme) => ({
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  filterRegion: {
    display: "flex",
    justifyContent: "flex-end",
    alignContent: "center",
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(1),
    marginLeft: "auto",
  },
  createEventButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.green[600],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: colors.green[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.green[600],
      },
    },
    "&:disabled": {
      visibility: "hidden",
      cursor: "not-allowed",
    },
  },
  dangerButton: {
    color: colors.red[600],
    border: `1px solid ${alpha(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: alpha(
        colors.red[600],
        theme.palette.action.hoverOpacity
      ),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  eventActions: {
    display: "flex",
    justifyContent: "space-between",
  },
  hidden: {
    visibility: "hidden",
  },
}));

export type EventEditorState = Partial<LoadedEventDefinition> & {
  typeSpecificFilterVal?: string;
  filters?: EventDefinitionFilter[];
};

export type EventEditorProps = {
  screenshot: string;
  editorState: EventEditorState;
  editorType: "create" | "edit";
  unsavedEdits: boolean;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  setState: React.Dispatch<React.SetStateAction<EventEditorState>>;
  setScreenshot: (screenshot: string) => void;
  onSave: () => void;
  onDelete: () => void;
  hasNetworkEventEditPerms: () => boolean;
  isNetworkDynProp: (snapshot: EventSnapshot) => boolean;
  hasJsDynPropEditPerms: () => boolean;
  destinations: LoadedDestinationInfo[];
  onClose: () => void;
};

export default function EventEditor({
  screenshot,
  editorState,
  editorType,
  unsavedEdits,
  errorMessage,
  setErrorMessage,
  setState,
  setScreenshot,
  onSave,
  onDelete,
  hasNetworkEventEditPerms,
  isNetworkDynProp,
  hasJsDynPropEditPerms,
  destinations,
  onClose,
}: EventEditorProps) {
  const alert = useAlert();
  const classes = useEventEditorStyles();
  const flag = useFeatureFlags();
  const currentEnvironment = useCurrentEnvironment();
  const [changeNwEventModalOpen, setChangeNwEventModalOpen] = useState<boolean>(
    false
  );
  const [changeNwEventNewType, setChangeNwEventNewType] = useState<{
    type?: EventType;
  }>({});
  const allSaasDestinations = useAllSaasDestinations();
  const allDestinations = { ...warehouseDestinations, ...allSaasDestinations };
  const destinationMetadataManager = useDestinationMetadataManager();
  const library = libraryForEventType(editorState.type);
  const validGADestinations = destinations.filter((dest) => {
    const destinationSlug =
      destinationKeyToSlugMap[dest.name] || dest.function_id;
    const destinationDefinition = allDestinations[destinationSlug];
    return (
      dest.enabled &&
      dest.name.startsWith("Google Analytics") &&
      destinationMetadataManager.destinationIsValidForEvent(
        dest,
        editorState.type
      ) &&
      (!destinationDefinition.supportedSources ||
        destinationDefinition.supportedSources[library])
    );
  });
  let nameHelperText = "";
  if (
    "name" in editorState &&
    typeof editorState.name === "string" &&
    editorState.name.length > 40 &&
    validGADestinations.length > 0
  ) {
    nameHelperText =
      "Note: Google Analytics will truncate the Event Name at 40 characters";
  }

  const handlePropertyChange = useCallback(
    function handlePropertyChange<T extends keyof EventEditorState>(
      prop: T,
      value: EventEditorState[T]
    ) {
      setState((editorState: EventEditorState) => {
        return _.extend<
          Partial<EventEditorState>,
          Partial<EventEditorState>,
          Partial<EventEditorState>
        >({}, editorState, { [prop]: value });
      });
    },
    [setState]
  );

  const setName = useCallback(
    function setName(e) {
      handlePropertyChange("name", e.target.value);
    },
    [handlePropertyChange]
  );

  const setDescription = useCallback(
    function setName(e) {
      handlePropertyChange("description", e.target.value);
    },
    [handlePropertyChange]
  );

  const setType = useCallback(
    function setType(e) {
      setErrorMessage("");
      setChangeNwEventNewType({});
      if (editorState.type === "network" && e.target.value !== "network") {
        let confirmNeeded = false;
        for (let uiFilter of editorState.filters || []) {
          if (isNetworkFilter(uiFilter)) {
            confirmNeeded = true;
          }
        }
        for (let snapshot of editorState.snapshots || []) {
          if (isNetworkDynProp(snapshot)) {
            confirmNeeded = true;
          }
        }
        if (confirmNeeded) {
          changeNwEventNewType.type = e.target.value;
          setChangeNwEventModalOpen(true);
          return;
        }
      }
      handlePropertyChange("type", e.target.value);
    },
    [
      setErrorMessage,
      editorState.type,
      editorState.filters,
      editorState.snapshots,
      handlePropertyChange,
      isNetworkDynProp,
      changeNwEventNewType,
    ]
  );

  const handleChangeNwEvent = useCallback(
    function handleChangeNetworkEvent() {
      //Delete network-specific filters and dyn props

      setState((editorState) => {
        let newFilters: EventDefinitionFilter[] = [];
        for (let uiFilter of editorState.filters || []) {
          if (!isNetworkFilter(uiFilter)) {
            newFilters.push(uiFilter);
          }
        }
        let newSnapshots: EventSnapshot[] = [];
        for (let snapshot of editorState.snapshots || []) {
          if (!isNetworkDynProp(snapshot)) {
            newSnapshots.push(snapshot);
          }
        }

        return _.extend({}, editorState, {
          type: changeNwEventNewType.type,
          filters: newFilters,
          snapshots: newSnapshots,
        });
      });
    },
    [changeNwEventNewType.type, setState, isNetworkDynProp]
  );

  function handleChangeNwEventModalCancel() {
    setChangeNwEventModalOpen(false);
  }

  const setTypeSpecificFilterVal = useCallback(
    function setTypeSpecificFilterVal(value) {
      handlePropertyChange("typeSpecificFilterVal", value);
    },
    [handlePropertyChange]
  );

  const setLibrary = useCallback(
    function setLibrary(e) {
      // We determine the library in use based on the event type. When you toggle
      // a new library, we just change the current event type to one from the
      // designated library.
      setErrorMessage("");
      if (e.target.value === "web") {
        handlePropertyChange("type", "click");
      } else if (e.target.value === "react-native") {
        handlePropertyChange("type", "touch");
      } else if (e.target.value === "sql") {
        handlePropertyChange("type", "scheduled_event");
      }
    },
    [setErrorMessage, handlePropertyChange]
  );

  function handleAddFilter() {
    setState((editorState) => {
      const newFilter: EventDefinitionFilter = {
        type: "prop_filter",
        prop: "",
        nw_path: "",
        op: "=",
        val: "",
      };

      const newFilters = (editorState.filters || []).concat([newFilter]);
      return _.extend({}, editorState, { filters: newFilters });
    });
  }

  function handleAddSnapshot() {
    setState((editorState) => {
      const newSnapshot: EventSnapshot = {
        name: "",
        type: "css_selector",
        value: "",
      };

      const newSnapshots = (editorState.snapshots || []).concat([newSnapshot]);
      return _.extend({}, editorState, { snapshots: newSnapshots });
    });
  }

  // optionsForLibrary returns the list of option components for the event type
  // dropdown.
  function optionsForLibrary(library) {
    if (library === "web") {
      return (
        <>
          <option value="click">Click</option>
          <option value="pageview">Pageview</option>
          <option value="submit">Form Submit</option>
          <option value="change">Change</option>
          {hasNetworkEventEditPerms() && (
            <>
              <option value="network">Network</option>
            </>
          )}
        </>
      );
    } else if (library === "react-native") {
      return (
        <>
          <option value="touch">Touch</option>
          <option value="text_edit">Edit Input</option>
          <option value="toggle">Toggle Switch</option>
          <option value="react_navigation_screenview">Screen View</option>
        </>
      );
    } else if (library === "sql") {
      return (
        <>
          <option value="scheduled_event">Scheduled Event</option>;
          <option value="scheduled_identify">Scheduled Identify</option>;
        </>
      );
    } else {
      return <></>;
    }
  }

  const setFilterListState = useCallback(
    function setFilterListState(updateFiltersFn) {
      setState((editorState) => {
        const newFilters = updateFiltersFn(editorState.filters);
        return _.extend({}, editorState, { filters: newFilters });
      });
    },
    [setState]
  );

  const setSnapshotListState = useCallback(
    function setSnapshotListState(updateSnapshotsFn) {
      setState((editorState) => {
        const newSnapshots = updateSnapshotsFn(editorState.snapshots);
        return _.extend({}, editorState, { snapshots: newSnapshots });
      });
    },
    [setState]
  );

  var typeSpecificFilterName;
  var typeSpecificFilterProp;
  var placeholderText;

  if (editorState.type === "pageview") {
    typeSpecificFilterName = "URL Path";
    typeSpecificFilterProp = "$pathname";
    placeholderText = "For example /about or /products/*";
  } else if (editorState.type === "react_navigation_screenview") {
    typeSpecificFilterName = "Screen View Route";
    typeSpecificFilterProp = "$screen_path";
  } else if (editorState.type === "network") {
    typeSpecificFilterName = "URL Path";
    typeSpecificFilterProp = "$pathname";
    placeholderText = "For example /item or /item/*";
  } else {
    typeSpecificFilterName = "CSS Selector";
    typeSpecificFilterProp = null;
  }

  const proteusEnabled =
    !currentEnvironment.loaded ||
    !!getFeatureFlag(currentEnvironment.project, "sqlTransformations");

  useEffect(() => {
    if (libraryForEventType(editorState.type) === "sql" && !proteusEnabled) {
      setErrorMessage(
        `Contact support@freshpaint.io to enable this feature for the ${currentEnvironment.project.name} project. Provide your project ID: ${currentEnvironment.project.ID}.`
      );
    }
  }, [editorState.type, setErrorMessage, currentEnvironment, proteusEnabled]);

  const [isSyncing, setSyncing] = useState(false);
  // syncStatus is the latest result of the batch event definition sync. This is
  // refreshed regularly by polling.
  const [syncStatus, setSyncStatus] = useState<SyncStatus | undefined>(
    undefined
  );

  // Lazily grab the initial sync status
  useEffect(() => {
    if (
      editorType === "create" ||
      libraryForEventType(editorState.type) !== "sql" ||
      !proteusEnabled
    ) {
      return;
    }
    if (syncStatus) {
      return;
    }
    makeRequest<SyncStatus>("get_batch_event_def_sync_status", {
      event_def_id: editorState.id,
    }).then((syncStatusResp) => {
      setSyncing((syncStatusResp.data.lock_value ?? "") !== "");
      setSyncStatus((status) => {
        if (status) {
          return status;
        }
        return syncStatusResp.data;
      });
    });
  });

  // Poll every 10 seconds to update the sync status
  useInterval(() => {
    if (
      editorType === "create" ||
      libraryForEventType(editorState.type) !== "sql" ||
      !proteusEnabled
    ) {
      return;
    }
    makeRequest<SyncStatus>("get_batch_event_def_sync_status", {
      event_def_id: editorState.id,
    }).then((syncStatusResp) => {
      setSyncing(syncStatusResp.data.lock_value !== "");
      if (
        !syncStatus ||
        syncStatusResp.data.last_completed_at !== syncStatus.last_completed_at
      ) {
        setSyncStatus(syncStatusResp.data);
        alert.success(
          "Scheduled Event Definition Sync completed: " +
            getSyncStatusString(syncStatusResp.data)
        );
      }
    });
  }, 10000);

  function onSyncNow() {
    if (unsavedEdits) {
      alert.error("You have unsaved event definition changes.");
      return;
    }

    setSyncing(true);
    makeRequest<{ error_message: string }>("sync_batch_event_def", {
      event_def_id: editorState.id,
    })
      .then((syncResponse) => {
        if (syncResponse.data.error_message) {
          // This tells the user if a sync was already in progress
          alert.success(syncResponse.data.error_message);
        } else {
          alert.success(
            "Scheduled Event Definition Sync initiated. This may take several minutes to complete."
          );
        }
      })
      .catch((error) => {
        setSyncing(false);
        alert.error("Something went wrong. Unable to sync event definition.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleDeleteScreenshot() {
    makeRequest("delete_event_def_screenshot", { event_def_id: editorState.id })
      .then(() => {
        setScreenshot("");
      })
      .catch((error) => {
        alert.error("Unable to remove screenshot");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  return (
    <Card>
      <CardHeader
        title={
          editorType === "create"
            ? "Create A New Event"
            : "Edit Event" + (unsavedEdits ? " (Unsaved Changes)" : "")
        }
        action={
          <EventEditorHeaderAction
            editorState={editorState}
            onClose={onClose}
          />
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              autoFocus={editorType === "create"}
              InputLabelProps={shrink}
              variant="outlined"
              label="Event Name"
              helperText={nameHelperText}
              name="name"
              onChange={setName}
              value={editorState.name}
            />
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Library"
              name="library"
              onChange={setLibrary}
              select
              SelectProps={native}
              value={libraryForEventType(editorState.type)}
              variant="outlined"
            >
              <option value="web">Web</option>
              <option value="react-native">React Native</option>
              {flag("sqlTransformations") && <option value="sql">SQL</option>}
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Event Type"
              name="event-type"
              onChange={setType}
              select
              SelectProps={native}
              value={editorState.type}
              variant="outlined"
            >
              {optionsForLibrary(libraryForEventType(editorState.type))}
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            {libraryForEventType(editorState.type) === "sql" ? (
              <Editor
                className="border-0 bg-gray-100 px-2 py-3 text-gray-900 mb-4 font-mono text-xs"
                padding={12}
                style={{ minHeight: 120 }}
                onValueChange={setTypeSpecificFilterVal}
                value={editorState.typeSpecificFilterVal || ""}
                placeholder="SELECT insert_id, user_id FROM events..."
                highlight={(code) => highlight(code, languages.sql, "sql")}
              />
            ) : (
              <AutocompletePropField
                propName={typeSpecificFilterProp}
                fullWidth
                autoComplete="off"
                variant="outlined"
                InputLabelProps={shrink}
                label={typeSpecificFilterName}
                placeholder={placeholderText}
                name="type-specific-filter-val"
                onChange={setTypeSpecificFilterVal}
                value={editorState.typeSpecificFilterVal ?? ""}
              />
            )}
            {errorMessage && (
              <p className="text-xs text-red-600">{errorMessage}</p>
            )}
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              multiline
              minRows={2}
              maxRows={5}
              label="Description (optional)"
              placeholder="Add a description for this event"
              name="description"
              onChange={setDescription}
              value={editorState.description}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      {libraryForEventType(editorState.type) !== "sql" && (
        <>
          <CardContent>
            <FilterList
              filters={editorState.filters}
              setState={setFilterListState}
              library={libraryForEventType(editorState.type)}
              eventType={editorState.type}
            />
            <div className={classes.filterRegion}>
              <Button
                color="default"
                onClick={handleAddFilter}
                variant="outlined"
              >
                <AddIcon className={classes.icon} />
                Add Filter
              </Button>
            </div>
          </CardContent>
          <Divider />
          <CardContent>
            <SnapshotList
              eventType={editorState.type!}
              snapshots={editorState.snapshots}
              setState={setSnapshotListState}
              isNetworkDynProp={isNetworkDynProp}
              hasNetworkEventEditPerms={hasNetworkEventEditPerms}
              hasJsDynPropEditPerms={hasJsDynPropEditPerms}
            />
            <div className={classes.filterRegion}>
              <Button
                color="default"
                onClick={handleAddSnapshot}
                variant="outlined"
              >
                <AddIcon className={classes.icon} />
                Add Dynamic Property
              </Button>
            </div>
          </CardContent>
        </>
      )}
      <Divider />
      <CardContent className={classes.eventActions}>
        <Button
          className={clsx(
            classes.dangerButton,
            editorType === "create" && classes.hidden
          )}
          onClick={onDelete}
        >
          Remove Event
        </Button>

        <Box className={classes.buttonGroup}>
          <Button
            className={classes.dangerButton}
            onClick={onClose}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            className={classes.createEventButton}
            onClick={onSave}
            variant="outlined"
            disabled={
              libraryForEventType(editorState.type) === "sql" && !proteusEnabled
            }
          >
            {editorType === "create" ? "Create Event" : "Save"}
          </Button>
        </Box>
      </CardContent>
      <ChangeNetworkEventModal
        open={changeNwEventModalOpen}
        onClose={handleChangeNwEventModalCancel}
        onConfirm={handleChangeNwEvent}
      />
      {screenshot && (
        <>
          <Divider />
          <CardHeader
            title="Screenshot Preview"
            style={{ "padding-bottom": 0 }}
          />
          <CardContent>
            <img src={screenshot} alt="Screenshot" />

            <Button
              className={clsx(classes.dangerButton)}
              onClick={handleDeleteScreenshot}
              style={{ marginTop: "15px" }}
            >
              Remove Screenshot
            </Button>
          </CardContent>
        </>
      )}
      {editorType !== "create" &&
        libraryForEventType(editorState.type) === "sql" && (
          <>
            <Divider />
            <CardContent className={classes.eventActions + " items-center"}>
              {syncStatus ? (
                <Typography variant="body1">
                  {getSyncStatusString(syncStatus)}
                </Typography>
              ) : (
                <div className="px-3 py-2 font-bold text-lg flex items-center rounded">
                  <RefreshIcon className="animate-spin mx-auto" />
                </div>
              )}
              {isSyncing && (
                <div className="px-3 py-2 font-bold text-lg flex items-center rounded">
                  <RefreshIcon className="animate-spin mx-auto" />
                </div>
              )}
              <Button
                color="primary"
                variant="outlined"
                onClick={onSyncNow}
                disabled={isSyncing || !proteusEnabled}
              >
                {isSyncing
                  ? "Sync In Progress"
                  : "Sync " + currentEnvironment.name + " Now"}
              </Button>
            </CardContent>
          </>
        )}
    </Card>
  );
}
